import {
  Avatar,
  Badge,
  Box,
  Collapse,
  Flex,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Spinner,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { RiCheckLine, RiFileList3Line } from 'react-icons/ri';
import { MdOutlineKeyboardArrowDown, MdOutlineSmartToy, MdCompareArrows } from 'react-icons/md';
import { FiChevronRight } from 'react-icons/fi';
import {
  PerspectiveBaseInfoFragment,
  useGetPerspectiveByDtTopicProjectLazyQuery,
  useGetPlanSimilaritiesLazyQuery,
} from '../../generated/graphql';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanText } from '../helivan/HelivanText';
import { getQualityColor, getQualityScheme, getSimilarityStyle, cleanPlanText } from './ProjectTabViewFaq';
import { humanizeTokens } from '../../utils/tokenEstimate';
import { formatVersionLabel } from '../../utils/formatDate';

interface PlanningInspectProps {
  allDigitalTwins: { id: string; name: string }[];
  digitalTwinId?: string;
  setDigitalTwinId: (id: string | undefined) => void;
  // Per-task perspectives for the current version (carry qualityScore + judgeNotes).
  perspectives: PerspectiveBaseInfoFragment[];
  // Other versions of this watch, for the optional "compare with" view.
  timelineProjects?: { id: string; dateCreated: string }[];
  projectId: string;
  // Monitored agent's generation tokens: this snapshot + cumulative across versions.
  evalTokens?: { snapshot: number; cumulative: number };
}

// Lightweight, dependency-free Markdown renderer for plan.md content. Handles the
// structures a plan actually uses: headings, bullet/numbered lists, **bold**, and
// blank-line spacing. Anything else falls through as plain text.
function renderPlan(text: string) {
  const lines = text.split('\n');
  const renderInline = (s: string) => {
    const parts = s.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).filter(Boolean);
    return parts.map((p, i) => {
      if (p.startsWith('**') && p.endsWith('**')) {
        return (
          <Text as="span" key={i} fontWeight={700} color={HelivanColor.text.blueDark}>
            {p.slice(2, -2)}
          </Text>
        );
      }
      if (p.startsWith('`') && p.endsWith('`')) {
        return (
          <Text as="span" key={i} fontFamily="mono" fontSize="12px" bg={HelivanColor.background.secondary} px={1} borderRadius="3px">
            {p.slice(1, -1)}
          </Text>
        );
      }
      return <Fragment key={i}>{p}</Fragment>;
    });
  };

  return lines.map((raw, idx) => {
    const line = raw.trimEnd();
    if (line.trim() === '') return <Box key={idx} h="6px" />;

    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      const level = heading[1].length;
      return (
        <Text
          key={idx}
          fontSize={level <= 1 ? '15px' : level === 2 ? '14px' : '13px'}
          fontWeight={700}
          color={HelivanColor.text.blueDark}
          mt={idx === 0 ? 0 : 2}
          mb={1}
        >
          {renderInline(heading[2])}
        </Text>
      );
    }

    const bullet = line.match(/^(\s*)[-*]\s+(.*)$/);
    if (bullet) {
      const indent = Math.floor(bullet[1].length / 2);
      return (
        <HStack key={idx} align="flex-start" spacing={2} pl={`${12 + indent * 16}px`} mb="2px">
          <Box mt="7px" w="4px" h="4px" borderRadius="full" bg={HelivanColor.text.blueMid} flexShrink={0} />
          <Text fontSize="13px" color={HelivanColor.text.blueDark} lineHeight="1.6">
            {renderInline(bullet[2])}
          </Text>
        </HStack>
      );
    }

    const numbered = line.match(/^(\s*)(\d+)\.\s+(.*)$/);
    if (numbered) {
      return (
        <HStack key={idx} align="flex-start" spacing={2} pl="12px" mb="2px">
          <Text fontSize="13px" fontWeight={600} color={HelivanColor.text.blueMid} flexShrink={0} mt="0px">
            {numbered[2]}.
          </Text>
          <Text fontSize="13px" color={HelivanColor.text.blueDark} lineHeight="1.6">
            {renderInline(numbered[3])}
          </Text>
        </HStack>
      );
    }

    return (
      <Text key={idx} fontSize="13px" color={HelivanColor.text.blueDark} lineHeight="1.6" mb="2px">
        {renderInline(line)}
      </Text>
    );
  });
}

const entityIdOf = (p: PerspectiveBaseInfoFragment) => p.customAgent?.id ?? p.digitalTwin?.id;

// judgeNotes is "Overall N/7 — rationale\n\nBlocking: …\n\nFraming 2/2 · …".
// Pull the rationale for the collapsed preview (the score badge already shows N/7).
const judgePreview = (notes?: string | null) => {
  if (!notes) return '';
  const first = notes.split('\n')[0];
  return first.replace(/^Overall\s+\d+(?:\.\d+)?\s*\/\s*7\s*[—-]\s*/i, '').trim() || first;
};

export const PlanningInspect = ({
  allDigitalTwins,
  digitalTwinId,
  setDigitalTwinId,
  perspectives,
  projectId,
  evalTokens,
  timelineProjects = [],
}: PlanningInspectProps) => {
  // Default to the first/only agent.
  useEffect(() => {
    if (!digitalTwinId && allDigitalTwins.length > 0) {
      setDigitalTwinId(allDigitalTwins[0].id);
    }
  }, [digitalTwinId, allDigitalTwins, setDigitalTwinId]);

  const selectedAgent = allDigitalTwins.find((a) => a.id === digitalTwinId);

  // Optional comparison: how each task's plan moved vs an earlier version (plan-vector
  // cosine), shown as a per-task similarity badge.
  const [compareVersionId, setCompareVersionId] = useState<string | null>(null);
  const otherVersions = useMemo(
    () => timelineProjects.filter((tp) => tp.id !== projectId),
    [timelineProjects, projectId],
  );
  const compareVersion = timelineProjects.find((tp) => tp.id === compareVersionId);
  // All snapshot dates, so version labels match the timeline x-axis resolution.
  const allVersionDates = useMemo(() => timelineProjects.map((tp) => tp.dateCreated), [timelineProjects]);
  const [getPlanSims, { data: simData }] = useGetPlanSimilaritiesLazyQuery({ fetchPolicy: 'network-only' });

  useEffect(() => {
    if (digitalTwinId && compareVersionId) {
      getPlanSims({ variables: { agentId: digitalTwinId, versionAId: projectId, versionBId: compareVersionId } });
    }
  }, [digitalTwinId, compareVersionId, projectId, getPlanSims]);

  const similarityByTask = useMemo(() => {
    const m = new Map<string, number | null>();
    (simData?.getPlanSimilarities ?? []).forEach((s) => m.set(s.task, s.similarity ?? null));
    return m;
  }, [simData]);
  const isComparing = !!compareVersionId;

  // Per-task perspectives for the selected agent (exclude the 'topic' overall row).
  const taskPerspectives = useMemo(
    () =>
      perspectives
        .filter((p) => p.topic !== 'topic' && entityIdOf(p) === digitalTwinId)
        .sort((a, b) => a.topic.localeCompare(b.topic)),
    [perspectives, digitalTwinId]
  );

  const avgQuality = useMemo(() => {
    const scores = taskPerspectives
      .map((p) => p.qualityScore)
      .filter((s): s is number => s != null);
    if (scores.length === 0) return null;
    return Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10;
  }, [taskPerspectives]);

  // Lazily-loaded plan text per task topic. `plans` = current version; `plansB` = the
  // compared version (only fetched while comparing).
  const [expanded, setExpanded] = useState<string | null>(null);
  const [plans, setPlans] = useState<Record<string, string>>({});
  const [plansB, setPlansB] = useState<Record<string, string>>({});
  const [loadingTopic, setLoadingTopic] = useState<string | null>(null);
  const [fetchPlan] = useGetPerspectiveByDtTopicProjectLazyQuery({ fetchPolicy: 'cache-first' });

  // Fetch the compared version's plan for a topic into `plansB`.
  const fetchComparedPlan = (topic: string, versionId: string) => {
    if (!digitalTwinId) return;
    fetchPlan({
      variables: { dtId: digitalTwinId, topic, projectId: versionId },
      onCompleted: (data) => {
        const answer = data.getPerspectiveByDtTopicProject?.opinions?.[0]?.answer ?? '';
        setPlansB((prev) => ({ ...prev, [topic]: cleanPlanText(answer) || 'No plan recorded.' }));
      },
      onError: () => {
        setPlansB((prev) => ({ ...prev, [topic]: 'Could not load this plan.' }));
      },
    });
  };

  // When the compared version (or agent) changes, drop stale B-plans and refetch for the
  // open topic so the right column reflects the current selection.
  useEffect(() => {
    setPlansB({});
    if (expanded && compareVersionId) {
      fetchComparedPlan(expanded, compareVersionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareVersionId, digitalTwinId]);

  const toggleTask = (topic: string) => {
    if (expanded === topic) {
      setExpanded(null);
      return;
    }
    setExpanded(topic);
    if (plans[topic] === undefined && digitalTwinId) {
      setLoadingTopic(topic);
      fetchPlan({
        variables: { dtId: digitalTwinId, topic, projectId },
        onCompleted: (data) => {
          const answer = data.getPerspectiveByDtTopicProject?.opinions?.[0]?.answer ?? '';
          setPlans((prev) => ({ ...prev, [topic]: cleanPlanText(answer) || 'No plan recorded.' }));
          setLoadingTopic(null);
        },
        onError: () => {
          setPlans((prev) => ({ ...prev, [topic]: 'Could not load this plan.' }));
          setLoadingTopic(null);
        },
      });
    }
    // While comparing, also load the earlier version's plan for the side-by-side view.
    if (compareVersionId && plansB[topic] === undefined) {
      fetchComparedPlan(topic, compareVersionId);
    }
  };

  return (
    <Flex width="full" height="full" flexDirection="column" maxWidth="900px" mx="auto">
      {/* Header */}
      <Flex
        width="full"
        flexDirection={{ base: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ base: 'stretch', md: 'center' }}
        gap={{ base: 3, md: 0 }}
        mb={4}
        pb={4}
        borderBottom="1px solid"
        borderBottomColor={HelivanColor.border.primary}
      >
        <HStack spacing={3}>
          <Flex width="36px" height="36px" borderRadius="8px" bg={HelivanColor.hover.lightBlue} alignItems="center" justifyContent="center">
            <RiFileList3Line size={18} color={HelivanColor.text.helivanBlue} />
          </Flex>
          <VStack align="start" spacing={0}>
            <HelivanText variant="smallBody+" colorOverride={HelivanColor.text.blueDark}>Plans</HelivanText>
            <HelivanText variant="tinyBody" colorOverride={HelivanColor.text.blueMid}>
              How the agent planned each task this snapshot
            </HelivanText>
          </VStack>
        </HStack>

        <HStack spacing={3} flexWrap="wrap" justifyContent={{ base: 'flex-start', md: 'flex-end' }}>
          {avgQuality != null && (
            <HStack
              spacing={2}
              px={3}
              py={2}
              borderRadius="8px"
              bg="white"
              border="1px solid"
              borderColor={HelivanColor.border.primary}
            >
              <Text fontSize="11px" color={HelivanColor.text.blueMid}>Avg readiness</Text>
              <Badge colorScheme={getQualityScheme(avgQuality)} fontSize="12px">{avgQuality} / 7</Badge>
            </HStack>
          )}

          {evalTokens && evalTokens.cumulative > 0 && (
            <Tooltip
              label="Agent generation tokens — this check-in and the cumulative total across all check-ins."
              placement="top"
              hasArrow
            >
              <HStack
                spacing={3}
                px={3}
                py={2}
                borderRadius="8px"
                bg="white"
                border="1px solid"
                borderColor={HelivanColor.border.primary}
                cursor="help"
              >
                <VStack align="start" spacing={0}>
                  <Text fontSize="10px" color={HelivanColor.text.blueMid}>This check-in</Text>
                  <Text fontSize="12px" fontWeight={600} color={HelivanColor.text.blueDark}>
                    {humanizeTokens(evalTokens.snapshot)}
                  </Text>
                </VStack>
                <Box w="1px" alignSelf="stretch" bg={HelivanColor.border.primary} />
                <VStack align="start" spacing={0}>
                  <Text fontSize="10px" color={HelivanColor.text.blueMid}>Total</Text>
                  <Text fontSize="12px" fontWeight={600} color={HelivanColor.text.blueDark}>
                    {humanizeTokens(evalTokens.cumulative)}
                  </Text>
                </VStack>
              </HStack>
            </Tooltip>
          )}

          {allDigitalTwins.length > 1 && (
            <Menu isLazy>
              <MenuButton
                as={Box}
                px={3}
                py={2}
                borderRadius="6px"
                bg="white"
                border="1px solid"
                borderColor={HelivanColor.border.primary}
                cursor="pointer"
                minWidth="180px"
                _hover={{ borderColor: HelivanColor.text.helivanBlue }}
              >
                <HStack justify="space-between">
                  <HStack spacing={2}>
                    <MdOutlineSmartToy size={14} color={HelivanColor.text.blueMid} />
                    <Text fontSize="13px" color={selectedAgent ? HelivanColor.text.blueDark : HelivanColor.text.blueMid} noOfLines={1} maxWidth="120px">
                      {selectedAgent ? selectedAgent.name : 'Select agent'}
                    </Text>
                  </HStack>
                  <MdOutlineKeyboardArrowDown size={16} color={HelivanColor.text.blueMid} />
                </HStack>
              </MenuButton>
              <Portal>
                <MenuList zIndex={1400} maxHeight="300px" overflowY="auto" py={2}>
                  {allDigitalTwins.map((agent) => (
                    <MenuItem key={agent.id} onClick={() => setDigitalTwinId(agent.id)} fontSize="13px" py={2} px={3}>
                      <HStack spacing={2} width="full" justify="space-between">
                        <HStack spacing={2}>
                          <MdOutlineSmartToy size={14} color={HelivanColor.text.blueMid} />
                          <Text color={HelivanColor.text.blueDark}>{agent.name}</Text>
                        </HStack>
                        {digitalTwinId === agent.id && <RiCheckLine size={14} color={HelivanColor.text.helivanBlue} />}
                      </HStack>
                    </MenuItem>
                  ))}
                </MenuList>
              </Portal>
            </Menu>
          )}

          {otherVersions.length > 0 && (
            <Menu isLazy>
              <MenuButton
                as={Box}
                px={3}
                py={2}
                borderRadius="6px"
                bg={isComparing ? HelivanColor.hover.lightBlue : 'white'}
                border="1px solid"
                borderColor={HelivanColor.border.primary}
                cursor="pointer"
                minWidth="150px"
                _hover={{ borderColor: HelivanColor.text.helivanBlue }}
              >
                <HStack justify="space-between">
                  <HStack spacing={2}>
                    <MdCompareArrows size={14} color={HelivanColor.text.blueMid} />
                    <Text fontSize="13px" color={HelivanColor.text.blueDark} noOfLines={1} maxWidth="110px">
                      {compareVersion ? `vs ${formatVersionLabel(allVersionDates, compareVersion.dateCreated)}` : 'Compare with…'}
                    </Text>
                  </HStack>
                  <MdOutlineKeyboardArrowDown size={16} color={HelivanColor.text.blueMid} />
                </HStack>
              </MenuButton>
              <Portal>
                <MenuList zIndex={1400} maxHeight="300px" overflowY="auto" py={2}>
                  <MenuItem onClick={() => setCompareVersionId(null)} fontSize="13px" py={2} px={3}>
                    <HStack spacing={2} width="full" justify="space-between">
                      <Text color={HelivanColor.text.blueDark}>None (single version)</Text>
                      {!compareVersionId && <RiCheckLine size={14} color={HelivanColor.text.helivanBlue} />}
                    </HStack>
                  </MenuItem>
                  {otherVersions.map((tp) => (
                    <MenuItem key={tp.id} onClick={() => setCompareVersionId(tp.id)} fontSize="13px" py={2} px={3}>
                      <HStack spacing={2} width="full" justify="space-between">
                        <Text color={HelivanColor.text.blueDark}>{formatVersionLabel(allVersionDates, tp.dateCreated)}</Text>
                        {compareVersionId === tp.id && <RiCheckLine size={14} color={HelivanColor.text.helivanBlue} />}
                      </HStack>
                    </MenuItem>
                  ))}
                </MenuList>
              </Portal>
            </Menu>
          )}
        </HStack>
      </Flex>

      {/* Task list */}
      <Flex flex={1} overflowY="auto" flexDirection="column">
        {taskPerspectives.length === 0 ? (
          <Flex width="full" py={16} justifyContent="center" alignItems="center" bg="white" border="1px solid" borderColor={HelivanColor.border.primary} borderRadius="12px">
            <VStack spacing={2}>
              <RiFileList3Line size={28} color={HelivanColor.text.blueMid} />
              <Text fontSize="14px" fontWeight="600" color={HelivanColor.text.blueDark}>No plans in this snapshot</Text>
              <Text fontSize="13px" color={HelivanColor.text.blueMid}>Run an update to have the agent plan its tasks.</Text>
            </VStack>
          </Flex>
        ) : (
          <VStack spacing={3} width="full" align="stretch">
            {taskPerspectives.map((p) => {
              const isOpen = expanded === p.topic;
              const score = p.qualityScore;
              return (
                <Box
                  key={p.id}
                  bg="white"
                  border="1px solid"
                  borderColor={HelivanColor.border.primary}
                  borderLeft="4px solid"
                  borderLeftColor={getQualityColor(score)}
                  borderRadius="12px"
                  overflow="hidden"
                >
                  {/* Task header (clickable) */}
                  <Flex
                    p={4}
                    align="center"
                    justify="space-between"
                    cursor="pointer"
                    onClick={() => toggleTask(p.topic)}
                    _hover={{ bg: HelivanColor.background.secondary }}
                    gap={3}
                  >
                    <HStack spacing={3} align="center" flex={1} minWidth={0}>
                      <Box
                        transform={isOpen ? 'rotate(90deg)' : 'none'}
                        transition="transform 0.15s"
                        color={HelivanColor.text.blueMid}
                        flexShrink={0}
                      >
                        <FiChevronRight size={16} />
                      </Box>
                      <VStack align="start" spacing={0} flex={1} minWidth={0}>
                        <Text fontSize="14px" fontWeight={600} color={HelivanColor.text.blueDark} noOfLines={1}>
                          {p.topic}
                        </Text>
                        {p.judgeNotes && (
                          <Text fontSize="12px" color={HelivanColor.text.blueMid} noOfLines={1}>
                            {judgePreview(p.judgeNotes)}
                          </Text>
                        )}
                      </VStack>
                    </HStack>
                    <HStack spacing={2} flexShrink={0}>
                      {isComparing && (() => {
                        const sim = getSimilarityStyle(similarityByTask.get(p.topic));
                        return (
                          <Tooltip label={`Plan similarity vs ${compareVersion ? formatVersionLabel(allVersionDates, compareVersion.dateCreated) : "earlier version"}`} hasArrow placement="top">
                            <Badge colorScheme={sim.scheme} fontSize="11px">{sim.label}</Badge>
                          </Tooltip>
                        );
                      })()}
                      {score != null && (
                        <Badge colorScheme={getQualityScheme(score)} fontSize="12px">
                          {Math.round(score)}/7
                        </Badge>
                      )}
                    </HStack>
                  </Flex>

                  {/* Plan body */}
                  <Collapse in={isOpen} animateOpacity>
                    <Box borderTop="1px solid" borderTopColor={HelivanColor.border.primary} p={4} bg={HelivanColor.background.primary}>
                      {/* Judge assessment */}
                      {p.judgeNotes && (
                        <Box
                          mb={4}
                          p={3}
                          bg="white"
                          border="1px solid"
                          borderColor={HelivanColor.border.primary}
                          borderLeft="3px solid"
                          borderLeftColor={getQualityColor(score)}
                          borderRadius="8px"
                        >
                          <Text fontSize="11px" fontWeight={700} color={HelivanColor.text.blueMid} mb={1} textTransform="uppercase" letterSpacing="0.03em">
                            Readiness assessment
                          </Text>
                          <Text fontSize="13px" color={HelivanColor.text.blueDark} lineHeight="1.6" whiteSpace="pre-wrap">
                            {p.judgeNotes}
                          </Text>
                        </Box>
                      )}
                      {/* Plan */}
                      {loadingTopic === p.topic ? (
                        <HStack spacing={2} py={4} justify="center">
                          <Spinner size="sm" color={HelivanColor.text.helivanBlue} />
                          <Text fontSize="13px" color={HelivanColor.text.blueMid}>Loading plan…</Text>
                        </HStack>
                      ) : isComparing ? (
                        <Box bg="white" border="1px solid" borderColor={HelivanColor.border.primary} borderRadius="10px" overflow="hidden">
                          <Flex flexDirection={{ base: 'column', md: 'row' }}>
                            <Box
                              flex={1}
                              p={4}
                              borderRight={{ base: 'none', md: '1px solid' }}
                              borderBottom={{ base: '1px solid', md: 'none' }}
                              borderColor={HelivanColor.border.primary}
                              minWidth={0}
                            >
                              <Text fontSize="10px" fontWeight={600} color={HelivanColor.text.blueMid} mb={2} letterSpacing="0.03em">CURRENT</Text>
                              {renderPlan(plans[p.topic] ?? 'No plan recorded.')}
                            </Box>
                            <Box flex={1} p={4} minWidth={0}>
                              <Text fontSize="10px" fontWeight={600} color={HelivanColor.text.blueMid} mb={2} letterSpacing="0.03em">
                                {compareVersion ? formatVersionLabel(allVersionDates, compareVersion.dateCreated).toUpperCase() : 'EARLIER'}
                              </Text>
                              {plansB[p.topic] === undefined ? (
                                <HStack spacing={2} py={4} justify="center">
                                  <Spinner size="sm" color={HelivanColor.text.helivanBlue} />
                                  <Text fontSize="13px" color={HelivanColor.text.blueMid}>Loading plan…</Text>
                                </HStack>
                              ) : (
                                renderPlan(plansB[p.topic])
                              )}
                            </Box>
                          </Flex>
                        </Box>
                      ) : (
                        <Box>{renderPlan(plans[p.topic] ?? '')}</Box>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </VStack>
        )}
      </Flex>
    </Flex>
  );
};
